import dayjs from "dayjs";
import { cloneDeep } from "lodash";

import { filterDateByRange } from "@/utils/filterDateByRange";

import type { ListTableColumnSortBy } from "@/components/elements";
import type {
  Auction,
  AuctionCar,
  AuctionCarTransaction,
  AuctionCarTransactionColumns,
  AuctionsChartPayload,
} from "@/types/auctions";
import type { DatesRange, PaginatedList } from "@/types/entity";
import type { AuctionQueryParams, CarQueryParams, TransactionQueryParams } from "./types";

export function getAuctionFiltered(response: PaginatedList<Auction>, params: AuctionQueryParams) {
  let data = cloneDeep(response!);
  const { parameters, page, pageSize, status } = params;
  let results = [...data.results];

  // Client-side status filtering
  if (status) {
    results = results.filter((auction) => auction.status === status);
  }
  // Client-side date range filtering
  if (parameters?.range) {
    const { from, to } = parameters.range;
    const rangeFrom = dayjs(from).startOf("day").unix();
    const rangeTo = dayjs(to).endOf("day").unix();

    results = results.filter(({ start_date, end_date }) => {
      const auctionStart = dayjs(start_date).startOf("day").unix();
      const auctionEnd = dayjs(end_date).endOf("day").unix();

      // Ranges [auctionStart, auctionEnd] and [rangeFrom, rangeTo] overlap when:
      // auctionStart <= rangeTo && rangeFrom <= auctionEnd
      return auctionStart <= rangeTo && rangeFrom <= auctionEnd;
    });
  }

  // Client-side pagination
  const startIndex = ((page || 1) - 1) * (pageSize || 25);
  const endIndex = startIndex + (pageSize || 25);
  data.total = results.length;
  data.results = results.slice(startIndex, endIndex);
  data.page = page || 1;
  data.page_size = pageSize || 25;

  return data;
}

export function getCarDataFiltered(response: PaginatedList<AuctionCar>, params: CarQueryParams) {
  let data = cloneDeep(response!);
  const { parameters, page, pageSize } = params;
  let results = [...data.results];

  // Client-side filtering
  if (parameters?.full_text) {
    results = results.filter(({ make_name, model_name }) => {
      const tokens = parameters.full_text!.toLowerCase().split(" ");
      return tokens.every(
        (query) =>
          make_name?.toLowerCase().includes(query) || model_name?.toLowerCase().includes(query)
      );
    });
  }

  // Client-side pagination
  const startIndex = ((page || 1) - 1) * (pageSize || 25);
  const endIndex = startIndex + (pageSize || 25);
  data.total = results.length;
  data.results = results.slice(startIndex, endIndex);
  data.page = page || 1;
  data.page_size = pageSize || 25;

  return data;
}

function sortDataBy(id: AuctionCarTransactionColumns, sortBy: ListTableColumnSortBy = "asc") {
  return (a: AuctionCarTransaction, b: AuctionCarTransaction) => {
    const aValue = a[id];
    const bValue = b[id];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortBy === "asc" ? -1 : 1;
    if (bValue == null) return sortBy === "asc" ? 1 : -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortBy === "asc" ? aValue - bValue : bValue - aValue;
    }

    return sortBy === "asc"
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  };
}

export function getTransactionDataFiltered(
  response: PaginatedList<AuctionCarTransaction>,
  params: TransactionQueryParams
) {
  let data = cloneDeep(response!);
  const { parameters, page, pageSize, sort } = params;
  let results = [...data.results];

  // Client-side filtering
  if (parameters?.condition) {
    results = results.filter(
      ({ condition }) => condition.toLocaleLowerCase() === parameters.condition?.toLocaleLowerCase()
    );
  }
  if (parameters?.full_text) {
    results = results.filter(({ auction_house, event, seller, type }) => {
      const tokens = parameters.full_text!.toLowerCase().split(" ");
      return tokens.every(
        (query) =>
          auction_house?.toLowerCase().includes(query) ||
          event?.toLowerCase().includes(query) ||
          seller?.toLowerCase().includes(query) ||
          type?.toLowerCase().includes(query)
      );
    });
  }

  // Client-side sorting
  if (sort) {
    const sorter = sortDataBy(sort.id, sort.sortBy);
    results = results.sort(sorter);
  }

  // Client-side pagination
  const startIndex = ((page || 1) - 1) * (pageSize || 25);
  const endIndex = startIndex + (pageSize || 25);
  data.total = results.length;
  data.results = results.slice(startIndex, endIndex);
  data.page = page || 1;
  data.page_size = pageSize || 25;

  return data;
}

export function getDataFilteredByRange(response: AuctionsChartPayload[], range?: DatesRange) {
  if (range) {
    return response.filter(filterDateByRange(range));
  }
  return response;
}
